import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { rsvpService } from "@/services/rsvp.service";

export default function PublicRsvp() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const presetStatus = searchParams.get("status");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  const [status, setStatus] = useState("");
  const [guestCount, setGuestCount] = useState("1");
  const [mealPreference, setMealPreference] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        setLoading(true);
        setError("");
        const data = await rsvpService.getInfo(token);
        setInfo(data);
        setStatus(presetStatus || data?.guest?.rsvp_status || "");
        setGuestCount(String(data?.guest?.guest_count || 1));
        setMealPreference(data?.guest?.meal_preference || "");
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "RSVP link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, presetStatus]);

  const weddingTitle = useMemo(() => {
    const w = info?.wedding;
    if (!w) return "Wedding RSVP";
    const names = [w.bride_name, w.groom_name].filter(Boolean).join(" & ");
    return names ? `${names}` : "Wedding RSVP";
  }, [info]);

  const handleSubmit = async (nextStatus) => {
    const finalStatus = nextStatus || status;
    if (!finalStatus) {
      toast.error("Please select your RSVP");
      return;
    }

    try {
      setSubmitting(true);
      await rsvpService.confirm({
        token,
        status: finalStatus,
        guestCount: guestCount ? parseInt(guestCount, 10) : null,
        mealPreference: mealPreference || null,
      });
      toast.success("RSVP updated. Thank you!");
      setStatus(finalStatus);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to update RSVP");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardContent className="p-6 text-center text-muted-foreground">Loading RSVP…</CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardContent className="p-6 space-y-3 text-center">
            <div className="text-lg font-semibold">RSVP link invalid</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" onClick={() => window.location.reload()}>
                Retry
              </Button>
              <Button asChild>
                <Link to="/login">Login</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const guestName = info?.guest?.name || "Guest";
  const weddingDate = info?.wedding?.wedding_date || "";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-lg">
        <CardContent className="p-6 space-y-5">
          <div className="text-center space-y-1">
            <div className="text-xl font-semibold">{weddingTitle}</div>
            {weddingDate && <div className="text-sm text-muted-foreground">{weddingDate}</div>}
          </div>

          <div className="text-sm text-muted-foreground text-center">
            Hi <span className="font-medium text-foreground">{guestName}</span>, please confirm your attendance.
          </div>

          <div className="grid gap-3">
            <Label>RSVP</Label>
            <Select value={status || ""} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select your RSVP" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="maybe">Maybe</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-3">
            <Label>Guest Count</Label>
            <Input value={guestCount} onChange={(e) => setGuestCount(e.target.value)} />
          </div>

          <div className="grid gap-3">
            <Label>Meal Preference (optional)</Label>
            <Input value={mealPreference} onChange={(e) => setMealPreference(e.target.value)} placeholder="e.g. Veg / Jain / Non-Veg" />
          </div>

          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={submitting}
              onClick={() => handleSubmit("confirmed")}
            >
              Confirm
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              disabled={submitting}
              onClick={() => handleSubmit("declined")}
            >
              Decline
            </Button>
          </div>

          <Button variant="secondary" disabled={submitting} onClick={() => handleSubmit()}>
            Save
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


